import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Banner } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";
import { chosenSites } from "app/common/services/chosenSites";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import AdvertisersTableCompare from "app/pages/website-analysis/analyze-publishers/advertisers/compare/AdvertisersTableCompare";
import AdvertisersTableSingle from "app/pages/website-analysis/analyze-publishers/advertisers/single/AdvertisersTableSingle";
import { SectionWrapper, StyledBox } from "app/pages/website-analysis/analyze-publishers/advertisers/StyledComponents";
import { AdsTrafficContainer } from "app/pages/website-analysis/analyze-publishers/common/ads-traffic/AdsTrafficContainer";
import { TotalVisits } from "app/pages/website-analysis/analyze-publishers/common/total-visits/TotalVisits";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const BannerStyled = styled(Banner)`
    margin-bottom: 24px;
    padding: 16px 24px;
    background-color: ${colorsPalettes.blue[100]};
`;

const BannerTitleText = styled(I18n)`
    margin-bottom: 8px;
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

const BannerSubTitleText = styled(I18n)`
    ${setFontVariant("body2", colorsPalettes.carbon[500])};
`;

export const NewSectionBanner = () => {
    const { params } = useCurrentStateAndParams();

    const onClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "try it now banner");
        void proNavigator.go("digitalsuite_website_publisher_overview", params);
    };

    return (
        <BannerStyled
            title={<BannerTitleText>monetization.new.section.banner.title</BannerTitleText>}
            subtitle={<BannerSubTitleText>monetization.new.section.banner.subtitle</BannerSubTitleText>}
            buttonText={translate("monetization.new.section.banner.cta")}
            onButtonClick={onClick}
            CustomIcon={
                <SWReactIcons
                    iconName={"megaphone"}
                    size="xxl"
                />
            }
        />
    );
};

export const AdvertisersContainer: FC = () => {
    const i18n = i18nFilter();
    const { params } = useCurrentStateAndParams();
    const isCompare = params.key.split(",").length > 1;
    const chosenItems = chosenSites.sitelistForLegend();
    const getLink = (key, value) =>
        proNavigator.href(proNavigator.current().name, {
            ...proNavigator.getParams(),
            [key]: value,
        });

    const TableProps = {
        i18n,
        getLink,
    };

    return isCompare ? (
        <>
            <NewSectionBanner />
            <SectionWrapper>
                <StyledBox
                    height={"340px"}
                    width={"100%"}
                    data-automation="adsTraffic"
                >
                    <AdsTrafficContainer chosenItems={chosenItems} />
                </StyledBox>
            </SectionWrapper>
            <AdvertisersTableCompare {...TableProps} />
        </>
    ) : (
        <>
            <NewSectionBanner />
            <SectionWrapper>
                <StyledBox
                    height={"340px"}
                    minWidth={"295px"}
                    width={"33%"}
                    marginRight={"24px"}
                    data-automation="totalVisits"
                >
                    <TotalVisits />
                </StyledBox>
                <StyledBox
                    height={"340px"}
                    minWidth={"575px"}
                    width={"66%"}
                    data-automation="adsTraffic"
                >
                    <AdsTrafficContainer chosenItems={chosenItems} />
                </StyledBox>
            </SectionWrapper>
            <AdvertisersTableSingle {...TableProps} />
        </>
    );
};
